import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePremiumFeature } from '../components/PremiumFeature';
import { 
    ArrowLeft, 
    Compass, 
    Zap, 
    MessageCircleHeart, 
    Users, 
    Bookmark,
    ChevronRight,
    Sparkles,
    Brain,
    Flower2,
    Square,
    Home,
    Send,
    Eye
} from 'lucide-react';
import { IconButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Container } from '../components/brand/BrandLayout';
import { CoRegulateMark } from '../components/Logo';
import { useSharedAI } from '../context/SharedAIContext';

const RegulationHome = () => {
    const navigate = useNavigate();
    const { canAccess } = useAuth();
    const { checkAccess } = usePremiumFeature();
    const { sendMessage, messages, isLoading } = useSharedAI();
    const [showAIChat, setShowAIChat] = useState(false);
    const [aiInput, setAiInput] = useState('');
    
    // Check if user just completed a tool (came from feedback)
    const justCompletedTool = new URLSearchParams(window.location.search).get('completed') === 'true';

    const handleAISend = async () => {
        if (!aiInput.trim() || isLoading) return;
        await sendMessage(aiInput);
        setAiInput('');
        setShowAIChat(true);
    };

    const tools = [
        {
            id: 'regulation-navigator',
            icon: Compass,
            title: 'Regulation Navigator',
            description: 'Guided support based on how you\'re feeling right now',
            path: '/regulate/regulation-navigator',
            variant: 'sage',          // Teal
            premium: false
        },
        {
            id: 'micro-reset',
            icon: Zap,
            title: 'Micro-Reset',
            description: 'Quick grounding moments that take just seconds',
            path: '/regulate/micro-reset',
            variant: 'rose',          // Coral
            premium: false
        },
        {
            id: 'box-breathing',
            icon: Square,
            title: 'Box Breathing',
            description: 'A simple 4-4-4-4 breathing pattern to calm your nervous system',
            path: '/regulate/box-breathing',
            variant: 'lavender',      // Dusty Rose
            premium: false
        },
        {
            id: 'calm-cycle',
            icon: Flower2,
            title: 'Calm Cycle',
            description: 'A slow, steady rhythm to help your system settle',
            path: '/regulate/breathing-orb',
            variant: 'neutral',       // Neutral
            premium: false,
            isNew: true
        },
        {
            id: 'still-sphere',
            icon: Flower2,
            title: 'Still Sphere',
            description: 'A warm, quiet presence for stillness without rhythm',
            path: '/regulate/still-sphere',
            variant: 'sage',          // Teal
            premium: false,
            isNew: true
        },
        {
            id: 'emdr',
            icon: Eye,
            title: 'EMDR/Bilateral Regulation',
            description: 'Gentle left-right movement to calm your mind and interrupt racing thoughts',
            path: '/regulate/emdr',
            variant: 'rose',          // Coral
            premium: false,
            isNew: true
        },
        {
            id: 'self-talk',
            icon: MessageCircleHeart,
            title: 'Self-Talk Reframes',
            description: 'Gentle words to shift harsh inner narratives',
            path: '/regulate/self-talk',
            variant: 'lavender',      // Dusty Rose
            premium: false
        },
        {
            id: 'saved',
            icon: Bookmark,
            title: 'Saved Favourites',
            description: 'Your personal collection of helpful moments',
            path: '/regulate/saved',
            variant: 'neutral',       // Neutral
            premium: false,
            requiredFeature: null
        }
    ];

    const handleToolClick = (tool) => {
        // Navigate directly - no premium check needed
        navigate(tool.path);
    };

    return (
        <div className="min-h-screen bg-transparent">
            {/* Header - Glass-morphism */}
            <header className="sticky top-0 z-50 glass-header">
                <Container size="sm" className="py-5 flex items-center justify-between">
                    <IconButton 
                        onClick={() => navigate('/home')}
                        data-testid="back-btn"
                        className="text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-[#FFBB98]/40 transition-all duration-200"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <Heading as="h1" size="text-lg" className="text-[#1D6B6B]">Regulation Tools</Heading>
                    <button
                        onClick={() => navigate('/home')}
                        className="hover:opacity-80 transition-opacity"
                        data-testid="header-logo"
                        aria-label="Go to dashboard"
                    >
                        <CoRegulateMark size={28} />
                    </button>
                </Container>
            </header>

            <main className="max-w-xl mx-auto px-6 py-8">
                {/* AI Guide Section - Contextual help */}
                <div className="mb-8 glass-card-sage rounded-2xl p-5 brand-fade-in">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#7D8E95] to-[#1D6B6B] flex items-center justify-center flex-shrink-0 icon-glow-sage">
                            <Sparkles className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                            <SmallText className="text-[#1D6B6B] font-semibold mb-1">Guide</SmallText>
                            {justCompletedTool ? (
                                <BodyText className="text-[#2A8585] leading-relaxed">
                                    Well done on completing that practice. If you'd like to continue regulating, choose another tool below. Or if you're feeling better, head back to your dashboard.
                                </BodyText>
                            ) : (
                                <BodyText className="text-[#2A8585] leading-relaxed">
                                    These tools are here to support you. Start with whatever feels most approachable right now.
                                </BodyText>
                            )}
                            
                            {/* AI Chat expansion */}
                            {showAIChat && messages.length > 0 && (
                                <div className="mt-4 space-y-3">
                                    {messages.slice(-3).map((msg, idx) => (
                                        <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                            <div className={`max-w-[90%] rounded-xl px-3 py-2 text-sm ${
                                                msg.role === 'user' 
                                                    ? 'bg-[#7D8E95]/20 text-[#1D6B6B]' 
                                                    : 'bg-white/60 text-[#1D6B6B]'
                                            }`}>
                                                {msg.content}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Quick input for AI */}
                            <div className="mt-4 flex gap-2">
                                <input
                                    type="text"
                                    value={aiInput}
                                    onChange={(e) => setAiInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAISend()}
                                    placeholder="Ask me anything..."
                                    className="flex-1 px-4 py-2 text-sm rounded-full bg-white/60 border border-white/40/60 focus:outline-none focus:border-[#7D8E95] text-[#1D6B6B] placeholder-[#7D8E95]"
                                    data-testid="ai-input"
                                />
                                <button
                                    onClick={handleAISend}
                                    disabled={!aiInput.trim() || isLoading}
                                    className="w-9 h-9 rounded-full bg-[#1D6B6B] text-white flex items-center justify-center hover:bg-[#145555] disabled:opacity-50 transition-colors"
                                    data-testid="ai-send-btn"
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Welcome Message */}
                <div className="text-center mb-10 brand-fade-in">
                    <div className="w-16 h-16 mx-auto mb-5 rounded-full glass-card-sage flex items-center justify-center icon-glow-sage">
                        <Compass className="w-8 h-8 text-[#1D6B6B]" />
                    </div>
                    <Heading as="h2" size="text-2xl" className="mb-3">
                        Your regulation toolkit
                    </Heading>
                    <BodyText className="text-[#2A8585] max-w-sm mx-auto">
                        Choose a tool that feels right for this moment. There's no wrong choice.
                    </BodyText>
                </div>

                {/* Tools Grid - Glass-morphism */}
                <div className="space-y-4">
                    {tools.map((tool, index) => {
                        const isLocked = tool.premium && tool.requiredFeature && !canAccess(tool.requiredFeature);
                        const showPremiumBadge = isLocked || tool.partialPremium;
                        
                        // Map variant to glass class
                        const variantGlassClasses = {
                            sage: 'glass-card-sage',
                            lavender: 'glass-card-lavender',
                            rose: 'glass-card-rose',
                            blue: 'glass-card-blue',
                            gold: 'glass-card-gold'
                        };
                        const glassClass = variantGlassClasses[tool.variant] || 'glass-card';
                        
                        return (
                            <div
                                key={tool.id}
                                className={`${glassClass} rounded-2xl p-5 cursor-pointer card-hover hover-shine brand-slide-up ${isLocked ? 'opacity-85' : ''}`}
                                style={{ animationDelay: `${index * 60}ms` }}
                                onClick={() => handleToolClick(tool)}
                                data-testid={`tool-${tool.id}`}
                            >
                                <div className="flex items-center gap-5">
                                    <IconContainer variant={tool.variant} size="lg" useGradient={true} className={showPremiumBadge ? 'relative' : ''}>
                                        <tool.icon className="w-6 h-6" />
                                        {showPremiumBadge && (
                                            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#7D8E95] flex items-center justify-center">
                                                <Sparkles className="w-3 h-3 text-white" />
                                            </div>
                                        )}
                                    </IconContainer>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Heading as="h3" size="text-base">
                                                {tool.title}
                                            </Heading>
                                            {tool.isNew && (
                                                <span className="px-2 py-0.5 text-xs rounded-full bg-[#1D6B6B]/20 text-[#1D6B6B] font-medium">
                                                    New
                                                </span>
                                            )}
                                            {showPremiumBadge && (
                                                <span className="px-2 py-0.5 text-xs rounded-full bg-[#7D8E95]/20 text-[#7D8E95] font-medium">
                                                    Premium
                                                </span>
                                            )}
                                        </div>
                                        <BodyText className="text-sm text-[#2A8585]">
                                            {tool.description}
                                        </BodyText>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-[#7D8E95] group-hover:text-[#7D8E95] transition-colors" />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Gentle Reminder */}
                <div className="mt-10 text-center brand-fade-in" style={{ animationDelay: '400ms' }}>
                    <SmallText className="text-[#7D8E95]">
                        Take your time. Your nervous system knows what it needs.
                    </SmallText>
                </div>

                {/* Return to Dashboard Button */}
                <div className="mt-8 brand-fade-in" style={{ animationDelay: '500ms' }}>
                    <button
                        onClick={() => navigate('/home')}
                        className="w-full py-4 px-6 rounded-2xl bg-[#1D6B6B] text-white font-semibold
                                 hover:bg-[#145555] transition-all shadow-md flex items-center justify-center gap-3"
                        data-testid="return-to-dashboard-btn"
                    >
                        <Home className="w-5 h-5" />
                        <span>Return to Dashboard</span>
                    </button>
                </div>
            </main>
        </div>
    );
};

export default RegulationHome;
